import { cn } from '@h2hspark/ui';
import * as React from 'react';

import type { ThreadMessage } from './types';

/**
 * The bubbles — `Web App.dc.html` §3.16, transcribed.
 *
 * Mine: `align-self:flex-end`, `max-width:78%` in the overlay and 74% in the connection detail's
 * card, `--spark-gradient` on white text, `border-radius:18px 18px 4px 18px`. Theirs: the mirror
 * of it on `--surface-card` with a subtle border and the body colour, the flat corner on the
 * left.
 *
 * ## One component for three surfaces
 *
 * The thread overlay, the phone's full-screen thread and the connection detail's Messages card
 * all draw the same conversation, and `MEM-025` AC-7's lesson applies to a rendering as much as
 * to a list: two components that look alike drift, and the one that drifts is the one nobody is
 * looking at. `variant` carries the two width figures §3.16 actually differs on and nothing else.
 *
 * ## What a bubble does not say
 *
 * No sender name and no avatar per bubble — the header names the one other person in the room,
 * and repeating it on every message is the prototype's own choice too. **No read receipt and no
 * delivery tick**: Phase 2 (the specification's §2.12 list), and the payload has nothing to draw
 * one from.
 *
 * `<time>` carries the machine-readable instant so the timestamp is available to a screen reader
 * and to the browser's own tooling; the rendered text is the short form.
 */
export function MessageBubbles({
  messages,
  stampFor,
  variant = 'overlay',
  className,
}: {
  readonly messages: ReadonlyArray<ThreadMessage>;
  /** How an instant is written. Passed in so the timezone is resolved once per screen. */
  readonly stampFor: (instant: string) => string | null;
  readonly variant?: 'overlay' | 'card';
  readonly className?: string;
}): React.ReactElement {
  return (
    <ol className={cn('flex flex-col gap-3', className)}>
      {messages.map((message) => {
        const stamp = stampFor(message.sentAt);
        return (
          <li
            key={message.messageId}
            className={cn(
              'flex max-w-[78%] flex-col',
              variant === 'card' ? 'max-w-[74%]' : '',
              message.mine ? 'self-end items-end' : 'self-start items-start',
            )}
          >
            <p
              className={cn(
                'whitespace-pre-wrap break-words px-[15px] py-3 text-sm leading-snug',
                message.mine
                  ? 'rounded-[18px_18px_4px_18px] bg-spark text-white'
                  : 'rounded-[18px_18px_18px_4px] border border-border-subtle bg-card text-text-body',
              )}
            >
              {message.body}
            </p>
            {stamp === null ? null : (
              <time
                dateTime={message.sentAt}
                className="mt-1 px-1 text-2xs text-text-subtle"
              >
                {stamp}
              </time>
            )}
          </li>
        );
      })}
    </ol>
  );
}
